import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card } from "../../../components/ui";
import { ApiError } from "../../../lib/api/client";
import { uploadVoiceOpenAIKey, type VoiceOpenAIKeyStatus } from "../api/voiceApi";

const validationLabel: Record<string, string> = {
  valid: "已驗證",
  unknown: "狀態未知",
  not_configured: "尚未設定",
};

const validationVariant: Record<string, string> = {
  valid: "success",
  unknown: "warning",
  not_configured: "secondary",
};

export function OpenAIKeyCard({ status }: { status: VoiceOpenAIKeyStatus | undefined }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File>();
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!file) {
      setError("請選擇 .txt 文字檔。");
      return;
    }
    setLoading(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const next = await uploadVoiceOpenAIKey(file);
      queryClient.setQueryData(["voice", "openai-key-status"], next);
      setNotice("OpenAI API Key 已更新並完成驗證。");
      setFile(undefined);
      const fileInput = form.elements.namedItem(
        "voice-openai-key-file",
      ) as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "API Key 上傳失敗，請稍後再試。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="OpenAI API Key">
      <p className="text-muted mb-3">
        上傳包含 OpenAI API Key 的 <code>.txt</code> UTF-8 純文字檔，系統會在後端驗證並加密保存。
      </p>
      {notice && <Alert variant="success">{notice}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className="text-muted">目前狀態</span>{" "}
          <Badge variant={validationVariant[status?.validation ?? "not_configured"] ?? "secondary"}>
            {validationLabel[status?.validation ?? "not_configured"] ?? "尚未設定"}
          </Badge>
        </div>
        <dl className="mb-0">
          <dt className="small text-muted mb-1">顯示用 Key</dt>
          <dd>{status?.maskedKey ?? "尚未設定"}</dd>
          <dt className="small text-muted mb-1">儲存來源</dt>
          <dd>{status?.source ?? "encrypted_file"}</dd>
        </dl>
      </div>
      <form onSubmit={(event) => void submit(event)}>
        <label className="form-label font-weight-bold" htmlFor="voice-openai-key-file">
          API Key 檔案
        </label>
        <input
          id="voice-openai-key-file"
          name="voice-openai-key-file"
          className="form-control mb-3"
          type="file"
          accept=".txt,text/plain"
          onChange={(event) => setFile(event.target.files?.[0])}
        />
        <Button type="submit" loading={loading}>
          上傳並驗證
        </Button>
      </form>
    </Card>
  );
}
